import type { EngineOption, JobStatus, SvgJob } from "../../lib/types";

export const ENGINE_OPTIONS: EngineOption[] = [
  { value: "auto", label: "自动识别", shortLabel: "Auto", hint: "根据文件扩展名选择渲染器", extension: null },
  { value: "tikz", label: "TikZ / PGF", shortLabel: "TikZ", hint: "数学教材图、几何与精细结构", extension: ".tex" },
  { value: "pgfplots", label: "PGFPlots", shortLabel: "Plots", hint: "函数、统计图与二维三维数据", extension: ".tex" },
  { value: "graphviz", label: "Graphviz DOT", shortLabel: "DOT", hint: "树、图、DAG、自动机与依赖", extension: ".dot" },
  { value: "d2", label: "D2", shortLabel: "D2", hint: "软件架构、模块关系与网络拓扑", extension: ".d2" },
];

const STATUS_LABELS: Record<JobStatus, string> = {
  created: "待上传",
  queued: "排队中",
  building: "绘制中",
  uploading: "保存结果",
  completed: "已完成",
  failed: "失败",
  cancelled: "已取消",
  expired: "已过期",
};

const STAGE_LABELS: Record<string, string> = {
  created: "等待上传源文件",
  "source-uploaded": "源文件已上传",
  queued: "已进入 GitHub Actions 队列",
  "runner-started": "构建运行器已启动",
  "preparing-source": "正在检查并准备源文件",
  "source-downloaded": "私有源文件已下载",
  "source-prepared": "源文件与资源已安全展开",
  rendering: "正在调用绘图引擎",
  rendered: "SVG 已生成，正在校验",
  "packaging-results": "正在打包 SVG 与清单",
  "uploading-results": "正在上传私有结果",
  "uploaded-results": "结果已上传，等待完成",
  "results-uploaded": "结果已上传，等待完成",
  completed: "SVG 结果已保存",
  failed: "绘图任务执行失败",
  "dispatch-failed": "无法启动 GitHub Actions",
  cancelled: "任务已取消",
  expired: "任务文件已过期",
};

const FALLBACK_PROGRESS: Record<JobStatus, number> = {
  created: 5,
  queued: 30,
  building: 65,
  uploading: 90,
  completed: 100,
  failed: 0,
  cancelled: 0,
  expired: 100,
};

export function getStatusLabel(job: Pick<SvgJob, "status">): string {
  return STATUS_LABELS[job.status];
}

export function getStageLabel(job: Pick<SvgJob, "status" | "progress_stage" | "progress">): string {
  const stage = job.progress_stage || job.progress || job.status;
  return STAGE_LABELS[stage] || STAGE_LABELS[job.status] || stage;
}

export function getProgress(job: Pick<SvgJob, "status" | "progress_percent">): number {
  const value = Number(job.progress_percent);
  if (Number.isFinite(value)) return Math.max(0, Math.min(100, Math.round(value)));
  return FALLBACK_PROGRESS[job.status];
}

export function isTerminal(job: Pick<SvgJob, "status">): boolean {
  return ["completed", "failed", "cancelled", "expired"].includes(job.status);
}

export function isActive(job: Pick<SvgJob, "status">): boolean {
  return !isTerminal(job);
}

export function canCancel(job: Pick<SvgJob, "status">): boolean {
  return job.status === "created" || job.status === "queued";
}

export function canRetry(job: Pick<SvgJob, "status">): boolean {
  return job.status === "completed" || job.status === "failed" || job.status === "cancelled";
}

export function statusTone(job: Pick<SvgJob, "status">): "neutral" | "active" | "success" | "danger" | "warning" {
  if (job.status === "completed") return "success";
  if (job.status === "failed" || job.status === "expired") return "danger";
  if (job.status === "cancelled") return "warning";
  if (job.status === "queued" || job.status === "building" || job.status === "uploading") return "active";
  return "neutral";
}
